import { useMemo } from 'react'
import { Columns3, RotateCcw, X } from 'lucide-react'
import clsx from 'clsx'
import type { DealSearchSchema } from './types'

/** The custom report builder: the operator decides which columns exist.
 *  Selection order is preserved, so the picker also determines column order in
 *  both the grid and the Excel export. */
export function ColumnPicker({
  schema,
  selected,
  onChange,
  onClose,
}: {
  schema: DealSearchSchema
  selected: string[]
  onChange: (columns: string[]) => void
  onClose: () => void
}) {
  const grouped = useMemo(() => {
    const map = new Map<string, DealSearchSchema['columns']>()
    for (const column of schema.columns) {
      map.set(column.group, [...(map.get(column.group) ?? []), column])
    }
    return [...map.entries()]
  }, [schema.columns])

  const toggle = (key: string) => {
    if (selected.includes(key)) {
      // Never leave the grid with zero columns — it would render as a broken
      // page rather than as an empty selection.
      if (selected.length === 1) return
      onChange(selected.filter((c) => c !== key))
    } else {
      onChange([...selected, key])
    }
  }

  return (
    <div
      role="dialog"
      aria-label="Choose columns"
      className="absolute right-0 top-full z-30 mt-1 w-[520px] rounded-lg border border-amber-300/15 bg-[#0d0d0d] shadow-2xl shadow-black/60"
    >
      <div className="flex items-center justify-between border-b border-amber-300/10 px-3 py-2">
        <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/80">
          <Columns3 size={11} /> Report columns
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onChange([...schema.defaultColumns])}
            className="flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-stone-500 transition hover:text-amber-200"
          >
            <RotateCcw size={10} /> Reset
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close column picker"
            className="rounded p-1 text-stone-500 transition hover:text-amber-200"
          >
            <X size={12} />
          </button>
        </div>
      </div>

      <div className="max-h-[340px] overflow-y-auto p-3">
        <div className="grid grid-cols-3 gap-x-4 gap-y-3">
          {grouped.map(([group, columns]) => (
            <div key={group}>
              <div className="mb-1 text-[9px] uppercase tracking-[0.16em] text-stone-600">
                {group}
              </div>
              <ul className="space-y-0.5">
                {columns.map((column) => {
                  const checked = selected.includes(column.key)
                  const order = selected.indexOf(column.key) + 1
                  return (
                    <li key={column.key}>
                      <label
                        className={clsx(
                          'flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 text-[11px] transition',
                          checked ? 'bg-amber-400/[0.08] text-stone-100' : 'text-stone-400 hover:bg-white/[0.02]',
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(column.key)}
                          className="h-3 w-3 accent-amber-400"
                        />
                        <span className="flex-1 truncate">{column.label}</span>
                        {checked && (
                          <span className="font-mono text-[9px] text-amber-300/60">{order}</span>
                        )}
                      </label>
                    </li>
                  )
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <p className="border-t border-amber-300/10 px-3 py-1.5 text-[9px] text-stone-600">
        {selected.length} columns · numbers show export order · applies to the grid and the Excel
        download
      </p>
    </div>
  )
}
